import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Phone, MessageCircle, MapPin, AlertTriangle, Calendar, CheckCircle2, ClipboardList } from "lucide-react";

function normalizePhoneForWa(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("33")) return digits;
  if (digits.startsWith("0")) return "33" + digits.slice(1);
  return digits;
}

export default function Relances() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/relances");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markDone = useCallback(async (visitId) => {
    if (!window.confirm("Marquer cette relance comme faite ?")) return;
    await api.post(`/visits/${visitId}/relance-done`);
    load();
  }, [load]);

  const overdue = items.filter((r) => r.is_overdue);
  const today = items.filter((r) => r.is_today);
  const upcoming = items.filter((r) => !r.is_overdue && !r.is_today);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Mes relances</h1>
        <p className="text-stone-500 text-sm mt-1">
          {items.length} relance{items.length > 1 ? "s" : ""} programmée{items.length > 1 ? "s" : ""}
        </p>
      </div>

      {loading ? (
        <div className="text-center text-stone-400 py-10">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-stone-200 p-10 text-center">
          <ClipboardList className="w-10 h-10 text-stone-300 mx-auto mb-2" strokeWidth={2} />
          <div className="font-heading font-black text-lg">Aucune relance prévue</div>
          <p className="text-stone-500 text-sm mt-1">
            Quand tu enregistres une visite avec une date de relance, elle apparaît ici.
          </p>
        </div>
      ) : (
        <>
          <Section
            title={`En retard (${overdue.length})`}
            tone="rose"
            icon={<AlertTriangle className="w-5 h-5" strokeWidth={2.5} />}
            items={overdue}
            onMarkDone={markDone}
          />
          <Section
            title={`Aujourd'hui (${today.length})`}
            tone="amber"
            icon={<Calendar className="w-5 h-5" strokeWidth={2.5} />}
            items={today}
            onMarkDone={markDone}
          />
          <Section
            title={`À venir (${upcoming.length})`}
            tone="emerald"
            icon={<Calendar className="w-5 h-5" strokeWidth={2.5} />}
            items={upcoming}
            onMarkDone={markDone}
          />
        </>
      )}
    </div>
  );
}

function Section({ title, tone, icon, items, onMarkDone }) {
  if (items.length === 0) return null;
  const toneClasses = {
    rose: "bg-rose-500 text-white shadow-rose-500/30",
    amber: "bg-amber-500 text-white shadow-amber-500/30",
    emerald: "bg-emerald-500 text-white shadow-emerald-500/30",
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-2xl grid place-items-center shadow-lg ${toneClasses[tone]}`}>{icon}</div>
        <h3 className="font-heading font-black text-lg">{title}</h3>
      </div>
      <div className="space-y-2">
        {items.map((r) => (
          <RelanceCard key={r.visit_id} r={r} onMarkDone={onMarkDone} />
        ))}
      </div>
    </div>
  );
}

function RelanceCard({ r, onMarkDone }) {
  const phoneWa = normalizePhoneForWa(r.client_phone);
  const dateStr = new Date(r.next_visit_date).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div className="bg-white rounded-3xl border border-stone-100 p-4 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-3 mb-2">
        <Link to={`/clients/${r.client_id}`} className="flex-1 min-w-0">
          <div className="font-heading font-black text-base truncate" data-testid={`relance-client-${r.visit_id}`}>
            {r.client_name}
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
            <Calendar className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className={`font-bold ${r.is_overdue ? "text-rose-600" : r.is_today ? "text-amber-600" : "text-stone-600"}`}>
              {r.is_overdue ? "Retard · " : r.is_today ? "Aujourd'hui · " : ""}
              {dateStr}
            </span>
            {r.client_city && <span>· {r.client_city}</span>}
          </div>
        </Link>
        <button
          type="button"
          onClick={() => onMarkDone(r.visit_id)}
          data-testid={`mark-done-${r.visit_id}`}
          title="Marquer comme fait"
          className="p-2 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-all active:scale-95"
        >
          <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {r.notes && (
        <p className="text-xs text-stone-600 mt-1 mb-3 italic line-clamp-2">{`"${r.notes}"`}</p>
      )}

      {/* Quick contact actions */}
      <div className="flex gap-2 mt-2">
        {r.client_phone && (
          <a
            href={`tel:${r.client_phone}`}
            data-testid={`relance-call-${r.visit_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-3 py-2 rounded-full active:scale-95 shadow-md shadow-emerald-500/20"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.8} /> Appeler
          </a>
        )}
        {phoneWa && (
          <a
            href={`https://wa.me/${phoneWa}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`relance-wa-${r.visit_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-xs px-3 py-2 rounded-full active:scale-95 shadow-md"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.8} /> WhatsApp
          </a>
        )}
        <Link
          to={`/clients/${r.client_id}`}
          data-testid={`relance-go-${r.visit_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs px-3 py-2 rounded-full active:scale-95 shadow-md"
        >
          <MapPin className="w-3.5 h-3.5" strokeWidth={2.8} /> Fiche
        </Link>
      </div>
    </div>
  );
}
